import { useEffect, useMemo, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { ProspectDocument, RelationshipType, SystemTranslationKeys } from '@common/entities';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isInsuranceEnabled } from 'config/insurance';
import { RootState } from 'state/store';

import { LeasingJourneyHeader } from 'components/header';
import { Loader } from 'components/Loader';
import { useSystemTranslation } from 'hooks';
import { getInsuranceCriteria, getProspectDocument, signProspectDocument } from 'services';
import { formatDateTime } from 'utils/format';
import handleError from 'utils/handleError';

import PdfRenderer from './PdfRenderer';

export function convertUTCDateToLocalDate(dateString: string) {
  let newDate = new Date(dateString);
  newDate = new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60 * 1000);

  const offset = newDate.getTimezoneOffset() / 60;
  const hours = newDate.getHours();

  newDate.setHours(hours - offset);

  return formatDateTime(newDate);
}

function PdfSigner() {
  const { tSystem } = useSystemTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId, propertyId, pageId } = useParams();
  const { relationshipType } = useSelector(
    (state: RootState) => state.application.applicant?.currentResponse,
  );
  const isPrimaryRelationship =
    relationshipType === undefined || relationshipType === RelationshipType.Primary;

  const [documentUrl, setDocumentUrl] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [remainingUnsigned, setRemainingUnsigned] = useState(0);

  const { firstName, lastName, digitalSignature } = location.state;

  const currentDocumentIndex = useMemo(
    () => location.state.documentList.findIndex((d: any) => d.id.toString() === pageId),
    [location.state.documentList, pageId],
  );

  const currentDocument = location.state.documentList[currentDocumentIndex];

  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

  const { data, error } = useQuery({
    queryKey: ['sign-prospect-document-get-document', pageId],
    queryFn: () => getProspectDocument(currentDocument.id),
    enabled: !!pageId,
  });

  useEffect(() => {
    if (data) {
      const fileData = new Blob([data.data], { type: 'application/pdf' });
      setDocumentUrl(URL.createObjectURL(fileData));
      setIsSigning(false);
      setRemainingUnsigned(location.state.documentList.filter((d: any) => !d.isSigned).length);
      setIsLoading(false);
    }
  }, [data, location.state.documentList]);

  if (error instanceof Error) {
    handleError(`Form Viewer - Error while signing PDF: ${error}`, 'Error signing PDF.');
  }

  const signDocument = async (id: number, fullName: string, childFileId?: number) => {
    const response = await signProspectDocument(id, fullName, childFileId);
    return response.data;
  };

  const navigateToEntry = () => {
    navigate(`/${orgId}/${propertyId}/entry`, { replace: true });
  };

  const afterAllLeaseDocumentsSigned = async () => {
    queryClient.invalidateQueries({ queryKey: ['prospect-documents'] });
    if (!isPrimaryRelationship) {
      navigateToEntry();
      return;
    }
    if (!isInsuranceEnabled()) {
      navigateToEntry();
      return;
    }
    try {
      const criteria = await getInsuranceCriteria();
      if (criteria.isInsuranceRequired && !criteria.isLeaseInsured) {
        navigate(`/${orgId}/${propertyId}/insurance-enrollment`, { replace: true });
      } else {
        navigateToEntry();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : tSystem(SystemTranslationKeys.documents.insuranceCriteriaFetchError);
      handleError(
        `Form Viewer - Error fetching insurance criteria after lease sign: ${err}`,
        message,
      );
      navigateToEntry();
    }
  };

  const handleSingleDocumentSigning = async () => {
    const response = await signDocument(currentDocument.id ?? 0, digitalSignature);
    currentDocument.isSigned = response.isSigned;
    currentDocument.signedDate = response.signedDate;
  };

  const handlePacketDocumentSigning = async () => {
    const { id, childFileId } = currentDocument.childFileDetails[0];

    const response = await signDocument(id ?? 0, digitalSignature, childFileId);
    currentDocument.isSigned = response.isSigned;
    currentDocument.signedDate = response.signedDate;
  };

  const handleDocumentSigning = async () => {
    try {
      setIsSigning(true);
      if (currentDocument.isPacket) {
        await handlePacketDocumentSigning();
      } else {
        await handleSingleDocumentSigning();
      }
      if (location.state.documentList.some((d: any) => d.requiresSigning && !d.isSigned)) {
        handleNextDocument(location.state.documentList);
      } else {
        await afterAllLeaseDocumentsSigned();
      }
    } catch (err) {
      handleError(`Form Viewer - Error while signing PDF: ${err}`, 'Error signing PDF.');
    } finally {
      setIsSigning(false);
    }
  };

  const handleNextDocument = async (updatedDocumentsList?: ProspectDocument[]) => {
    const nextDocumentIndex =
      location.state.documentList.length === currentDocumentIndex + 1
        ? 0
        : currentDocumentIndex + 1;

    navigateToDocument(
      nextDocumentIndex,
      updatedDocumentsList
        ?.filter((d) => d.requiresSigning)
        ?.sort((d1, d2) => (!d1.isSigned && d2.isSigned ? -1 : 1)),
    );
  };

  const handlePreviousDocument = async () => {
    const previousDocumentIndex =
      location.state.documentList.length === currentDocumentIndex > 0
        ? currentDocumentIndex - 1
        : 0;

    navigateToDocument(previousDocumentIndex);
  };

  function navigateToDocument(documentIndex: number, updatedDocumentsList?: ProspectDocument[]) {
    queryClient.invalidateQueries({ queryKey: ['signProspectDocument'] });
    setIsLoading(true);
    const documentId = location.state.documentList[documentIndex].id.toString();
    const navigationState = {
      replace: true,
      state: {
        documentId,
        documentList: updatedDocumentsList ?? location.state.documentList,
        firstName,
        lastName,
        digitalSignature,
      },
    };
    navigate(`/${orgId}/${propertyId}/signPdf/${documentId}`, navigationState);
  }

  return (
    <Flex flexDir="column" h="100%">
      <LeasingJourneyHeader />
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        marginLeft={{ base: 2, md: 5 }}
        marginTop={{ base: '100px' }}
      >
        <Flex direction="column">
          <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="semibold">
            {`${currentDocument.name}`}
          </Text>
          <Text fontStyle="italic" mt={1} fontSize={{ base: 'xs', sm: 'sm' }} fontWeight="semibold">
            {`${remainingUnsigned} of ${location.state.documentList.length} unsigned documents`}
          </Text>
        </Flex>
        {!currentDocument.isSigned && (
          <Button
            size={{ base: 'sm', md: 'md' }}
            onClick={() => handleDocumentSigning()}
            bg="yellow.700"
            color="black"
            mr={2}
            _hover={{
              bg: 'yellow.700',
            }}
          >
            {tSystem(SystemTranslationKeys.documents.signDocument)}
          </Button>
        )}
        {currentDocument.isSigned && (
          <Box
            height={9}
            bg="green.100"
            marginTop={20}
            marginRight={20}
            borderRadius="md"
            padding={2}
          >
            <Text fontSize="sm">
              {`${tSystem(SystemTranslationKeys.documents.signed)}: ${convertUTCDateToLocalDate(currentDocument.signedDate)}`}
            </Text>
          </Box>
        )}
      </Flex>
      {!isSigning && (isLoading || !documentUrl) && (
        <Loader label={tSystem(SystemTranslationKeys.documents.viewingDocument)} />
      )}
      {isSigning && <Loader label={tSystem(SystemTranslationKeys.documents.signingDocument)} />}
      {!isLoading && documentUrl && (
        <PdfRenderer
          enablePaging={false}
          documentUrl={documentUrl}
          documentName={currentDocument.name}
          showFooter
        >
          {location.state.documentList.length > 1 ? (
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              marginBottom={5}
              marginRight={10}
            >
              <Button
                size={{ sm: 'sm', md: 'md' }}
                px={{ base: 2, md: 4 }}
                py={{ base: 2, md: 4 }}
                marginLeft={{ base: 4, md: 10 }}
                onClick={() => handlePreviousDocument()}
                bg="black"
                _hover={{ bg: 'black' }}
                color="white"
                isDisabled={currentDocumentIndex === 0}
              >
                Previous
              </Button>
              <Button
                size={{ sm: 'sm', md: 'md' }}
                px={{ base: 2, md: 4 }}
                py={{ base: 2, md: 4 }}
                marginRight={{ base: 4, md: 10 }}
                onClick={() => handleNextDocument()}
                bg="black"
                _hover={{
                  bg: 'black',
                }}
                color="white"
                isDisabled={currentDocumentIndex === location.state.documentList.length - 1}
              >
                Next
              </Button>
            </Flex>
          ) : null}
        </PdfRenderer>
      )}
    </Flex>
  );
}

export default PdfSigner;
